import { useRef, useState, useMemo, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { lotteryApi } from '../../api/lottery.js';
import { Spinner, ErrorBox, Field } from '../../components/ui.js';
import { text, money, num, datetime } from '../../lib/format.js';
import { LotteryGradeBadge } from '../../components/LotteryGradeBadge.js';
import { useAddressSearch } from '../../lib/daumPostcode.js';
import { useAutoGrowTextarea } from '../../lib/hooks.js';
import type { OrderDetail, OrderPrize } from '../../api/types.js';

/**
 * 원본: lottery/order/lottery_order_detail.php (팝업 창으로 열리던 화면).
 * 팝업 대신 라우트로 만들어서 뒤로가기·새로고침·URL 공유가 되게 했다.
 *
 * 화면 구성은 랜덤박스 주문 상세(randombox/OrderDetailPage.tsx)와 맞췄다 -
 * 주문 정보 · 회원 카드, 당첨 경품 표, 배송 정보 · 처리 폼 순서이고, 받는분 ·
 * 배송지는 원본과 같이 다음 우편번호 위젯(주소찾기)으로 고친다.
 *
 * 이 화면은 데이터에 남은 일본어 UI 텍스트(주문상태 표시용 별칭 · イメージ 같은
 * 표 헤더)를 전부 한국어로 바꿔 보여준다. 실제 상품명(jp_name)처럼 그 자체가
 * 일본어 고유명사인 값은 번역하지 않는다 - 상품 데이터를 훼손하지 않기 위해서다.
 * 당첨 경품 표는 랜덤박스 쪽(등급 · 경품명 · 수량) 레이아웃에 맞추되, 랜덤박스에는
 * 없는 이미지 칸만 그대로 남긴다(경품 이미지는 이 화면에서 계속 봐야 한다). 등급
 * 색은 레이아웃만 빌려온 randombox 배색이 아니라, 원본 lottery/product/
 * prize_list.php 의 A~E·L 배색(components/LotteryGradeBadge.tsx)을 쓴다 -
 * 이 화면의 등급 체계가 애초에 그쪽과 같다.
 *
 * 저장은 배송지(lottery_member) · 주문(lottery_orders) · 경품별 배송취소
 * (lottery_order_details)를 한 트랜잭션으로 묶는다 - 원본은 두 테이블을 조인한
 * UPDATE 한 방이었고, 여기서도 "전부 반영되거나 전부 취소되거나" 는 같다.
 *
 * 경품별 배송취소는 랜덤박스(RbOrderDetailPage)와 같은 UI·동작이다. 예전에는
 * lottery_order_details 에 status 컬럼이 없어 못 옮겼는데,
 * migrations/005_lottery_order_details_status.sql 이 그 컬럼을 만든다 -
 * **그 마이그레이션을 적용하기 전에는 이 화면의 조회부터 실패한다.**
 */
export function OrderDetailPage() {
  const { orderNo = '' } = useParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['lottery', 'order', orderNo],
    queryFn: () => lotteryApi.order(orderNo),
    enabled: Boolean(orderNo),
  });

  if (query.isPending) return <Spinner />;
  if (query.isError) return <ErrorBox error={query.error} onRetry={() => void query.refetch()} />;

  return <OrderEditor key={orderNo} orderNo={orderNo} data={query.data} queryClient={queryClient} />;
}

/**
 * 서버(lottery/orders.routes.ts)는 레거시가 섞어 쓰던 '미배송'/'未発送' 을 표시
 * 단계에서 '未発送' 하나로 통일해 API 로 내려준다(목록 화면은 그 값을 그대로
 * 쓴다). 이 화면만 한국어로 봐야 하므로 여기서 다시 되돌린다 - 목록 쪽 표시나
 * 서버 응답 자체는 건드리지 않는다.
 */
const STATUS_KO: Record<string, string> = { 未発送: '미배송' };
function statusKo(value: string | null): string | null {
  return value === null ? null : (STATUS_KO[value] ?? value);
}

/** card_status 는 결제 게이트웨이가 주는 원문(success 등) 그대로다. 화면엔 한국어로 보여준다. */
const CARD_STATUS_KO: Record<string, string> = { success: '결제완료' };
function cardStatusKo(value: string | null): string | null {
  return value === null ? null : (CARD_STATUS_KO[value] ?? value);
}

/**
 * 목록에서 클릭했을 때는 OrderDetailModal 이, 주소를 직접 열었을 때는 위의
 * OrderDetailPage(전체 화면)가 이 컴포넌트를 그대로 재사용한다 - onClose 유무로
 * "뒤로 가기 링크" 대 "닫기 버튼"만 갈린다. (랜덤박스 쪽과 같은 구조다.)
 */
export function OrderEditor({
  orderNo,
  data,
  queryClient,
  onClose,
}: {
  orderNo: string;
  data: OrderDetail;
  queryClient: ReturnType<typeof useQueryClient>;
  onClose?: () => void;
}) {
  const { order, shipping, prizes, totalQuantity } = data;

  // 주문상태 · 메모는 원본과 같이 "손댄 값만" 보낸다 - null 이면 아직 손대지 않은
  // 것이다. 목록/상세가 '미배송' 을 '未発送' 로 바꿔 보여주기 때문에, 손대지 않은
  // 상태까지 되돌려 보내면 DB 값이 조용히 일본어로 바뀐다.
  const [memo, setMemo] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  // 배송지는 원본 폼과 같이 다섯 칸을 한 벌로 다룬다.
  const [ship, setShip] = useState({
    name: shipping?.name ?? '',
    phone: shipping?.phone ?? '',
    zipcode: shipping?.zipcode ?? '',
    address1: shipping?.address1 ?? '',
    address2: shipping?.address2 ?? '',
  });

  const shipDirty =
    shipping !== null &&
    (ship.name !== (shipping.name ?? '') ||
      ship.phone !== (shipping.phone ?? '') ||
      ship.zipcode !== (shipping.zipcode ?? '') ||
      ship.address1 !== (shipping.address1 ?? '') ||
      ship.address2 !== (shipping.address2 ?? ''));

  // 경품별 배송취소. randombox 쪽(RbOrderDetailPage)과 같은 방식이다 - 체크 상태를
  // 들고 있다가 저장할 때 "새로 취소된 것" 과 "되돌린 것" 만 골라 보낸다.
  const originalCanceled = useMemo(() => {
    const map: Record<number, boolean> = {};
    for (const p of prizes) if (p.seq !== null) map[p.seq] = p.status === 'CANCELED';
    return map;
  }, [prizes]);
  const [canceled, setCanceled] = useState<Record<number, boolean>>(originalCanceled);

  const { cancelSeqs, restoreSeqs } = useMemo(() => {
    const cancel: number[] = [];
    const restore: number[] = [];
    for (const [seqStr, isCanceled] of Object.entries(canceled)) {
      const seq = Number(seqStr);
      const was = originalCanceled[seq] ?? false;
      if (isCanceled && !was) cancel.push(seq);
      if (!isCanceled && was) restore.push(seq);
    }
    return { cancelSeqs: cancel, restoreSeqs: restore };
  }, [canceled, originalCanceled]);

  const dirty =
    memo !== null || status !== null || shipDirty || cancelSeqs.length > 0 || restoreSeqs.length > 0;

  const mutation = useMutation({
    mutationFn: () =>
      lotteryApi.updateOrder(orderNo, {
        ...(memo !== null ? { memo } : {}),
        ...(status !== null ? { orderStatus: status } : {}),
        ...(shipDirty ? { shipping: ship } : {}),
        ...(cancelSeqs.length > 0 ? { cancelSeqs } : {}),
        ...(restoreSeqs.length > 0 ? { restoreSeqs } : {}),
      }),
    onSuccess: async () => {
      setMemo(null);
      setStatus(null);
      // 배송지는 되돌리지 않는다 - 아래 invalidate 로 새 값이 내려오면
      // shipDirty 가 저절로 false 가 된다.
      await queryClient.invalidateQueries({ queryKey: ['lottery', 'order', orderNo] });
      await queryClient.invalidateQueries({ queryKey: ['lottery', 'orders'] });
    },
  });

  const setShipField = (key: keyof typeof ship) => (value: string) =>
    setShip((prev) => ({ ...prev, [key]: value }));

  const memoRef = useAutoGrowTextarea(memo ?? order.memo ?? '');
  const addressSearch = useAddressSearch();
  const addr2Ref = useRef<HTMLInputElement>(null);

  function openAddressSearch() {
    addressSearch.open((addr) => {
      setShip((prev) => ({
        ...prev,
        zipcode: addr.zonecode,
        address1: addr.roadAddress || addr.jibunAddress,
      }));
      // 원본과 같이 주소를 고르면 상세주소 칸으로 바로 넘어간다.
      addr2Ref.current?.focus();
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!dirty) return;
    mutation.mutate();
  }

  return (
    <section className={onClose ? undefined : 'page--half-right'}>
      <header className="page-head">
        <div>
          {onClose ? (
            <button type="button" className="link link--back" onClick={onClose}>
              ← 목록으로
            </button>
          ) : (
            <Link className="link link--back" to="/lottery/orders">
              ← 주문 목록
            </Link>
          )}
          <h1 className="page-title">
            주문 {text(order.orderNo)}
            <span className="page-title__sub">{text(order.product.name)}</span>
          </h1>
        </div>
        {onClose && (
          <button type="button" className="modal-close modal-close--static" onClick={onClose} aria-label="닫기">
            ×
          </button>
        )}
      </header>

      <div className="detail-grid">
        <div className="card">
          <h2 className="card__title">주문 정보</h2>
          <dl className="deflist">
            <dt>주문일</dt><dd>{datetime(order.orderDate)}</dd>
            <dt>최종 수정</dt><dd>{datetime(order.updatedAt)}</dd>
            <dt>랜덤박스</dt>
            <dd>
              {order.kuji ? (
                <Link className="link" to={`/lottery/products/${order.kuji}/prizes`}>
                  {text(order.product.name)}
                </Link>
              ) : (
                text(order.product.name)
              )}
              {order.isOnahole && <span className="tag">오나홀</span>}
            </dd>
            <dt>티켓</dt><dd>{num(order.ticket)}</dd>
            <dt>단가</dt><dd>{money(order.unitAmount)}</dd>
            <dt>배송비</dt><dd>{money(order.deliveryPrice)}</dd>
            <dt>합계</dt><dd className="strong">{money(order.totalAmount)}</dd>
          </dl>
        </div>

        <div className="card">
          <h2 className="card__title">회원</h2>
          <dl className="deflist">
            {/* 회원ID·이메일은 banana.shop_member 에만 있는 값이라 여기서는 볼 수
                없다. 이 화면이 보는 lottery_member 에는 회원번호와 수취인 정보뿐이다. */}
            <dt>회원번호</dt><dd>{order.member.idx === null ? '-' : String(order.member.idx)}</dd>
            <dt>수취인명</dt><dd>{text(order.member.name)}</dd>
            <dt>누적 결제액</dt><dd>{money(shipping?.totalAmountPrice ?? null)}</dd>
            <dt>결제 상태</dt><dd>{text(cardStatusKo(order.cardStatus))}</dd>
            <dt>주문 상태</dt><dd><span className="badge">{text(statusKo(order.orderStatus))}</span></dd>
          </dl>
        </div>
      </div>

      <div className="card">
        <h2 className="card__title">
          당첨 경품
          <span className="card__count">
            {prizes.length}건 / 총 {totalQuantity}개 · 취소{' '}
            {Object.values(canceled).filter(Boolean).length}건
          </span>
        </h2>
        {prizes.length === 0 ? (
          <p className="td-muted">기록된 경품이 없습니다.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>등급</th>
                  <th>경품명</th>
                  <th>이미지</th>
                  <th style={{ textAlign: 'right' }}>수량</th>
                  <th>상태</th>
                  <th>배송 취소</th>
                </tr>
              </thead>
              <tbody>
                {prizes.map((p: OrderPrize, i: number) => {
                  const seq = p.seq;
                  const isCanceled = seq !== null ? (canceled[seq] ?? false) : false;
                  const changed = seq !== null && isCanceled !== (originalCanceled[seq] ?? false);
                  return (
                    <tr key={`${p.prizeId}-${p.prizeNo}-${i}`} className={changed ? 'row--changed' : undefined}>
                      <td><LotteryGradeBadge grade={p.grade} /></td>
                      <td className={isCanceled ? 'td-struck' : undefined}>{text(p.title)}</td>
                      <td>{p.thumbnail ? <img className="thumb thumb--lg" src={p.thumbnail} alt="" loading="lazy" /> : '-'}</td>
                      <td className="td-right">{num(p.quantity)}</td>
                      <td>
                        <span className={isCanceled ? 'badge badge--danger' : 'badge'}>
                          {isCanceled ? 'CANCELED' : 'READY'}
                        </span>
                        {changed && <span className="td-sub">변경됨</span>}
                      </td>
                      <td>
                        <label className="checkbox">
                          <input
                            type="checkbox"
                            checked={isCanceled}
                            disabled={seq === null || mutation.isPending}
                            onChange={(e) => {
                              if (seq === null) return;
                              setCanceled((prev) => ({ ...prev, [seq]: e.target.checked }));
                            }}
                          />
                          <span>취소</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {/*
          저장하면 "전부 취소" 가 되는 경우에만 알린다. cancelSeqs 는 이번에 새로
          체크한 것만 담으므로 그것만 세면 안 된다 - 이미 취소돼 있던 줄까지 합쳐
          모든 경품이 취소 상태인지를 본다.
        */}
        {cancelSeqs.length > 0 &&
          prizes.filter((p) => p.seq !== null).every((p) => canceled[p.seq!]) && (
            <p className="hint hint--warn">
              경품을 전부 취소하면 주문 상태가 <strong>주문취소</strong>로 바뀌어, 배송처리용 CSV·
              송장입력 대상에서 빠집니다.
            </p>
          )}
      </div>

      <form onSubmit={onSubmit}>
        <div className="card">
          <h2 className="card__title">배송 정보</h2>

          {shipping ? (
            <>
              <h3 className="subhead">받는분</h3>
              <div className="form-grid">
                <Field label="받는분 이름 *">
                  <input
                    className="input"
                    value={ship.name}
                    onChange={(e) => setShipField('name')(e.target.value)}
                    required
                    maxLength={64}
                  />
                </Field>
                <Field label="받는분 연락처 *">
                  <input
                    className="input"
                    value={ship.phone}
                    onChange={(e) => setShipField('phone')(e.target.value)}
                    required
                    maxLength={32}
                  />
                </Field>
              </div>

              <h3 className="subhead">배송지</h3>
              <div className="address-row">
                <Field label="우편번호 *">
                  <input
                    className="input"
                    value={ship.zipcode}
                    readOnly
                    placeholder="주소찾기로 입력"
                    required
                    maxLength={16}
                  />
                </Field>
                <button type="button" className="btn btn--ghost address-row__btn" onClick={openAddressSearch}>
                  {addressSearch.loading ? '불러오는 중…' : '주소찾기'}
                </button>
              </div>
              {addressSearch.error && <p className="qty-msg qty-msg--error">{addressSearch.error}</p>}
              <div className="form-grid">
                <Field label="주소 *">
                  <input
                    className="input"
                    value={ship.address1}
                    readOnly
                    placeholder="주소찾기로 입력"
                    required
                    maxLength={255}
                  />
                </Field>
                <Field label="상세주소">
                  <input
                    ref={addr2Ref}
                    className="input"
                    value={ship.address2}
                    onChange={(e) => setShipField('address2')(e.target.value)}
                    maxLength={255}
                  />
                </Field>
              </div>
            </>
          ) : (
            /* lottery_member 에 이 회차의 행이 없는 주문. 원본도 이런 주문은
               UPDATE 가 0건이라 저장이 안 됐다 - 입력칸을 아예 안 그린다. */
            <p className="td-muted">등록된 배송지가 없어 수정할 수 없습니다.</p>
          )}

          <h3 className="subhead">처리</h3>
          <div className="form-grid">
            {/* 랜덤박스는 미배송/배송완료 셀렉트지만, くじ 쪽은 원본이 임의 문자열을
                넣어온 이력이 있어(未発送 · 미배송 · 주문취소 …) 자유 입력을 유지한다. */}
            <Field label="주문 상태">
              <input
                className="input"
                value={status ?? statusKo(order.orderStatus) ?? ''}
                onChange={(e) => setStatus(e.target.value)}
                maxLength={40}
              />
            </Field>
            <Field label="메모" full>
              <textarea
                ref={memoRef}
                className="input input--area input--auto-grow"
                rows={2}
                value={memo ?? order.memo ?? ''}
                onChange={(e) => setMemo(e.target.value)}
                maxLength={2000}
              />
            </Field>
          </div>

          {mutation.isError && <ErrorBox error={mutation.error} />}
          {mutation.isSuccess && !dirty && <p className="ok-note">저장되었습니다.</p>}

          {shipDirty && (
            <p className="hint hint--warn">
              저장하면 배송지와 주문정보가 <strong>한 트랜잭션으로 함께</strong> 반영됩니다.
              하나라도 실패하면 전부 취소됩니다.
            </p>
          )}

          <div className="editor__actions">
            <button
              type="button"
              className="btn btn--ghost"
              disabled={!dirty || mutation.isPending}
              onClick={() => {
                setMemo(null);
                setStatus(null);
                setShip({
                  name: shipping?.name ?? '',
                  phone: shipping?.phone ?? '',
                  zipcode: shipping?.zipcode ?? '',
                  address1: shipping?.address1 ?? '',
                  address2: shipping?.address2 ?? '',
                });
                mutation.reset();
              }}
            >
              되돌리기
            </button>
            <button className="btn btn--primary" type="submit" disabled={!dirty || mutation.isPending}>
              {mutation.isPending ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}
